import { useRef, useState } from 'react';

import type { EmployeeSearchProps } from '@/common/components/shared/employee-search/props';
import { InputSearch } from '@/common/components/shared/input-search/InputSearch';
import { BusinessUnitSelectMultiple } from '@/common/components/shared/select/business-unit/BusinessUnitSelect';
import { CompanySelectMultiple } from '@/common/components/shared/select/company/CompanySelect';
import { CountrySelectMultiple } from '@/common/components/shared/select/country/CountrySelect';
import { DepartmentSelectMultiple } from '@/common/components/shared/select/department/DepartmentSelect';
import { EmployeeStatusSelectMultiple } from '@/common/components/shared/select/employee-status/EmployeeStatusSelect';
import { JobSelectMultiple } from '@/common/components/shared/select/job/JobSelect';
import { JobLevelSelectMultiple } from '@/common/components/shared/select/job-level/JobLevelSelect';
import type { ListEmployeesFilterInput } from '@/common/graphql';
import type { EmployeeSearchFilterType } from '@/common/types';

import { createAllowlistFilter } from './EmployeeSearchForm.utils';

type FilterKey = 'statuses' | 'levels' | 'companyIds' | 'countryIds' | 'businessUnitIds' | 'departmentIds' | 'jobIds';

interface Props {
  baseVariables: ListEmployeesFilterInput;
  filters: EmployeeSearchFilterType;
  searchFilters?: EmployeeSearchProps['searchFilters'];
  onChange: (patch: Partial<EmployeeSearchFilterType>) => void;
}

export const EmployeeSearchFilters = ({ baseVariables, filters, searchFilters, onChange }: Props) => {
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const [optionsCounts, setOptionsCounts] = useState<Partial<Record<FilterKey, number>>>({});

  const setOptionsCount = (key: FilterKey) => (count: number) =>
    setOptionsCounts(prev => (prev[key] === count ? prev : { ...prev, [key]: count }));

  // Each `disabled={optionsCounts.X === 1}` below auto-disables a filter dropdown whenever only
  // one option remains selectable for it - whether because `baseVariables`' own allowlist (via
  // `createAllowlistFilter`) narrowed it to a single option, or because the underlying dataset
  // itself only has one item. Either way, there's nothing left to choose between, so the control
  // is disabled rather than presenting an interactive dropdown with only one choice.
  return (
    <fieldset className="-mx-2 mb-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
      <InputSearch
        innerRef={keywordInputRef}
        placeholder="Keyword search"
        value={filters.keyword}
        onClear={() => onChange({ keyword: '' })}
        onSearch={text => onChange({ keyword: text })}
      />
      <EmployeeStatusSelectMultiple
        disabled={optionsCounts.statuses === 1}
        filter={createAllowlistFilter(baseVariables.statuses)}
        placeholder="Status"
        value={filters.statuses}
        onChange={newValue => onChange({ statuses: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('statuses')}
      />
      <JobLevelSelectMultiple
        disabled={optionsCounts.levels === 1}
        filter={createAllowlistFilter(baseVariables.levels)}
        placeholder="Level"
        value={filters.levels}
        onChange={newValue => onChange({ levels: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('levels')}
      />
      <CountrySelectMultiple
        disabled={optionsCounts.countryIds === 1}
        filter={createAllowlistFilter(baseVariables.countryIds)}
        placeholder="Country"
        selectableDisabledItems={true}
        value={filters.countryIds}
        onChange={newValue => onChange({ countryIds: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('countryIds')}
      />
      <CompanySelectMultiple
        disabled={optionsCounts.companyIds === 1}
        filter={createAllowlistFilter(baseVariables.companyIds)}
        placeholder="Company"
        selectableDisabledItems={true}
        value={filters.companyIds}
        variables={{ enabled: true, hasEmployees: true, ...(searchFilters?.company || {}) }}
        onChange={newValue => onChange({ companyIds: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('companyIds')}
      />
      <BusinessUnitSelectMultiple
        disabled={optionsCounts.businessUnitIds === 1}
        filter={createAllowlistFilter(baseVariables.businessUnitIds)}
        placeholder="Business Unit"
        selectableDisabledItems={true}
        value={filters.businessUnitIds}
        variables={{ hasEmployees: true }}
        onChange={newValue => onChange({ businessUnitIds: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('businessUnitIds')}
      />
      <DepartmentSelectMultiple
        disabled={optionsCounts.departmentIds === 1}
        filter={createAllowlistFilter(baseVariables.departmentIds)}
        placeholder="Department"
        selectableDisabledItems={true}
        value={filters.departmentIds}
        variables={{ hasEmployees: true }}
        onChange={newValue => onChange({ departmentIds: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('departmentIds')}
      />
      <JobSelectMultiple
        disabled={optionsCounts.jobIds === 1}
        filter={createAllowlistFilter(baseVariables.jobIds)}
        placeholder="Job"
        selectableDisabledItems={true}
        value={filters.jobIds}
        onChange={newValue => onChange({ jobIds: newValue.map(({ value: v }) => v) })}
        onOptionsCountChange={setOptionsCount('jobIds')}
      />
    </fieldset>
  );
};
